// frontend/src/features/livestock/TimelineEntryCard.tsx
/**
 * A single entry on the organism timeline.
 *
 * Layout with photo:
 * ┌────────────────────────────────────────┐
 * │  20 Feb 2026                    [···]  │
 * │                                        │
 * │  ┌──────────────┐                      │
 * │  │              │                      │
 * │  │    photo     │  Entry text goes     │
 * │  │  thumbnail   │  here alongside      │
 * │  │              │  the photo.          │
 * │  └──────────────┘                      │
 * │                                        │
 * └────────────────────────────────────────┘
 *
 * Layout without photo:
 * ┌────────────────────────────────────────┐
 * │  20 Feb 2026                    [···]  │
 * │  Entry text spans full width.          │
 * └────────────────────────────────────────┘
 */

import { useState } from 'react';
import {
  Text,
  Group,
  ActionIcon,
  Badge,
  Menu,
  Box,
  Image,
  UnstyledButton,
} from '@mantine/core';
import { IconDots, IconFocusCentered, IconPencil, IconTrash } from '@tabler/icons-react';
import { timelineApi } from '@/api/timeline';
import type { LargePhoto } from '@/types/tankPhotos';
import type { TimelineEntry } from '@/types/timeline';
import { PhotoLightbox } from './PhotoLightbox';
import type { ImageSize } from './imageSize';

interface TimelineEntryCardProps {
  /** The organism's tank; lets the photo viewer load a larger copy past 2x. */
  tankId?: string;
  entry: TimelineEntry;
  imageSize?: ImageSize;
  onEdit?: (entry: TimelineEntry) => void;
  onDelete?: (entryId: number) => void;
  /**
   * Opens the photo in the caller's viewer (the organism page's, with Line up);
   * the card's own plain lightbox is then not rendered.
   */
  onOpenPhoto?: (entry: TimelineEntry) => void;
}

function formatEntryDate(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function TimelineEntryCard({
  tankId,
  entry,
  imageSize = 'medium',
  onEdit,
  onDelete,
  onOpenPhoto,
}: TimelineEntryCardProps): JSX.Element {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const openPhoto = (): void => {
    if (onOpenPhoto) onOpenPhoto(entry);
    else setLightboxOpen(true);
  };

  const hasPhoto = !!entry.photo_thumb_url;
  const hasText = !!entry.entry_text;

  // Determine image source based on size
  const imgSrc =
    imageSize === 'thumbnail'
      ? entry.photo_thumb_url
      : imageSize === 'medium'
        ? entry.photo_display_url ?? entry.photo_url
        : entry.photo_url;
  const isCompact = imageSize === 'thumbnail';

  return (
    <>
      <Box>
        {/* Date and actions row */}
        <Group justify="space-between" align="flex-start" gap="xs" wrap="nowrap">
          <Group gap="xs" wrap="wrap" miw={0}>
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              style={{
                fontFamily: 'Space Grotesk, sans-serif',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatEntryDate(entry.entry_date)}
            </Text>
            {hasPhoto && entry.framing && (
              <Badge
                variant="light"
                color="teal"
                size="xs"
                leftSection={<IconFocusCentered size={10} />}
                data-testid="lined-up-badge"
              >
                Lined up
              </Badge>
            )}
          </Group>

          {(onEdit || onDelete) && (
            <Menu width={120} position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="md"
                  aria-label={`Actions for ${formatEntryDate(entry.entry_date)} entry`}
                >
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {onEdit && (
                  <Menu.Item
                    leftSection={<IconPencil size={14} />}
                    onClick={() => onEdit(entry)}
                  >
                    Edit
                  </Menu.Item>
                )}
                {onDelete && (
                  <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    color="red"
                    onClick={() => onDelete(entry.id)}
                  >
                    Delete
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>

        {/* Content: photo + text */}
        {hasPhoto && isCompact && hasText ? (
          /* Thumbnail: photo + text side by side */
          <Group align="flex-start" gap="sm" wrap="nowrap" mt="xs">
            <UnstyledButton onClick={openPhoto}>
              <Image
                src={imgSrc}
                alt={`Photo from ${formatEntryDate(entry.entry_date)}`}
                w={160}
                h={160}
                fit="cover"
                radius="md"
                loading="lazy"
                style={{ cursor: 'pointer', flexShrink: 0 }}
              />
            </UnstyledButton>
            <Text size="sm" style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
              {entry.entry_text}
            </Text>
          </Group>
        ) : hasPhoto && isCompact ? (
          /* Thumbnail: photo only */
          <Box mt="xs">
            <UnstyledButton onClick={openPhoto}>
              <Image
                src={imgSrc}
                alt={`Photo from ${formatEntryDate(entry.entry_date)}`}
                w={160}
                h={160}
                fit="cover"
                radius="md"
                loading="lazy"
                style={{ cursor: 'pointer' }}
              />
            </UnstyledButton>
          </Box>
        ) : hasPhoto ? (
          /* Medium or Full: photo stacked above text */
          <Box mt="xs">
            <UnstyledButton
              onClick={openPhoto}
              style={{ display: 'block', width: '100%' }}
            >
              <Image
                src={imgSrc}
                alt={`Photo from ${formatEntryDate(entry.entry_date)}`}
                w="100%"
                maw={imageSize === 'medium' ? 400 : undefined}
                fit={imageSize === 'full' ? 'contain' : 'cover'}
                radius="md"
                loading="lazy"
                style={{
                  cursor: 'pointer',
                  aspectRatio: imageSize === 'medium' ? '4 / 3' : undefined,
                }}
              />
            </UnstyledButton>
            {hasText && (
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} mt="xs">
                {entry.entry_text}
              </Text>
            )}
          </Box>
        ) : hasText ? (
          /* Text only */
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} mt="xs">
            {entry.entry_text}
          </Text>
        ) : null}
      </Box>

      {/* Full-screen lightbox, unless the caller opens photos in its own viewer */}
      {!onOpenPhoto && (
        <PhotoLightbox
          photoUrl={entry.photo_url}
          photoKey={entry.id}
          opened={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          loadLarge={
            tankId && entry.photo_url
              ? (): Promise<LargePhoto | null> =>
                  timelineApi.large(tankId, entry.livestock_id, entry.id)
              : undefined
          }
        />
      )}
    </>
  );
}
